// Renders a JSON string as a syntax-highlighted, dark code block that stays LTR inside an RTL page.
//
// The tricky part is bidi: raw JSON dropped into an RTL container gets its braces/commas reordered
// (}‎ jumps to the top, commas lead each line, etc.). The fix (same as the old automation's
// PayloadPreview): render EACH line as its own dir="ltr" block with unicode-bidi:isolate, so the
// bidi algorithm can't reorder tokens across lines, and turn leading whitespace into a fixed-width
// inline-block so indentation survives.

import {Box} from "@mui/material";
import {borders} from "../../ui/colors";

const JSON_THEME = {
    key: "#7DD3FC",       // light sky — property names
    string: "#86EFAC",    // mint — string values
    number: "#FCD34D",    // amber — numbers
    boolean: "#FCA5A5",   // soft red — true/false
    null: "#94A3B8",      // muted grey — null
    punct: "#E2E8F0",     // off-white — braces, commas, colons
};

// Tokenize one line of pre-formatted JSON into colored spans (classic MDN JSON regex).
const highlightJson = (rawJson) => {
    const tokenRegex = /("(\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g;
    const out = [];
    let lastIndex = 0;
    let match;
    let i = 0;
    while ((match = tokenRegex.exec(rawJson)) !== null) {
        if (match.index > lastIndex) {
            out.push(<span key={`p${i++}`} style={{color: JSON_THEME.punct}}>{rawJson.slice(lastIndex, match.index)}</span>);
        }
        const token = match[0];
        let color;
        if (/^"/.test(token)) {
            color = /:$/.test(token) ? JSON_THEME.key : JSON_THEME.string;
        } else if (/true|false/.test(token)) {
            color = JSON_THEME.boolean;
        } else if (/null/.test(token)) {
            color = JSON_THEME.null;
        } else {
            color = JSON_THEME.number;
        }
        out.push(<span key={`t${i++}`} style={{color}}>{token}</span>);
        lastIndex = match.index + token.length;
    }
    if (lastIndex < rawJson.length) {
        out.push(<span key={`p${i++}`} style={{color: JSON_THEME.punct}}>{rawJson.slice(lastIndex)}</span>);
    }
    return out;
};

const renderLines = (text) => text.split("\n").map((line, idx) => {
    const indent = (line.match(/^(\s*)/) || ["", ""])[1];
    const content = line.slice(indent.length);
    return (
        <div key={idx} dir="ltr" style={{direction: "ltr", unicodeBidi: "isolate", textAlign: "left", whiteSpace: "pre"}}>
            {indent.length > 0 && <span style={{display: "inline-block", width: `${indent.length * 0.6}em`}}/>}
            {content.length > 0 ? highlightJson(content) : "​"}
        </div>
    );
});

/**
 * A syntax-highlighted JSON code block that renders correctly inside an RTL page. Matches the old
 * automation's PayloadPreview: the whole block is a dir="ltr", unicode-bidi:isolate <pre>, and each
 * line is additionally its own LTR isolate. `header` renders above the JSON (e.g. the request line).
 */
export default function JsonBlock({json, header}) {
    return (
        <Box
            component="pre"
            dir="ltr"
            sx={{
                direction: "ltr",
                unicodeBidi: "isolate",
                textAlign: "left",
                width: "100%",
                boxSizing: "border-box",
                m: 0,
                p: 1.5,
                bgcolor: "#0F172A",
                border: borders.default,
                borderRadius: borders.radius.sm,
                fontSize: "12px",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                lineHeight: 1.6,
                overflowX: "auto",
                maxHeight: 420,
                overflowY: "auto",
            }}
        >
            {header && <div dir="ltr" style={{direction: "ltr", textAlign: "left", color: "#7DD3FC", marginBottom: 8}}>{header}</div>}
            {renderLines(json || "")}
        </Box>
    );
}
